import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import { catchError, concatMap, filter, first, map } from 'rxjs/operators';

import { ConfigurationService } from '../../../../COMMON/services/configuration.service';
import { PageHitEvent } from '../../../models/events/page-hit-event.model';
import { ZenitEventName } from '../../../models/zenit-general.model';
import { ZenitEventApiService } from '../../../services/api/zenit-event-api.service';
import { ZenitEventParserService } from '../../../services/parser/zenit-event-parser.service';
import { ZenitPageHitEventAction, ZenitEventSuccess, ZenitEventError, MarkReferrerSent } from '../../actions/zenit.actions';

import { CorePlatformService } from '@core/infrastructure/platform/platform.service';
import { SetPage } from '@core/infrastructure/state/core-commands.actions';
import { getCmsConfigValue, getPage } from '@core/infrastructure/state/core.selectors';
import { SetPickupPointsViewMode } from '@pages/checkout/shipping/store/pickup/pickup.actions';
import { SetColorOnSelectedProduct } from '@pages/product/store/product-page.actions';
import { ChangeTextSearchAction, ExecuteSearchAction } from '@pages/search/infrastructure/state/search.actions';
import { EventFrom } from '@shared/analytics/COMMON/models/analytics-common.models';
import { SetPickupDisplayModeAnalytics } from '@shared/analytics/store/app-events-analytics.actions';
import { modalsIncluded } from '@shared/analytics/ZENIT/configs/event.configs';
import { SetShopCartSuccess } from '@shared/shopcart/store/shopcart.actions';
import { OpenModalFromComponent } from '@ui/dialog/store/modal.actions';

@Injectable()
export class PageHitEventEffects {
    constructor(
        private readonly actions$: Actions,
        private readonly zenitEventParserService: ZenitEventParserService,
        private readonly zenitEventApiService: ZenitEventApiService,
        private readonly configurationService: ConfigurationService,
        private readonly platform: CorePlatformService,
        private readonly store: Store,
    ) { }

    public setPage$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(SetPage),
            filter(() => this.platform.isBrowser()),
            filter(action => !!action.page),
            concatMap((action) => this.configurationService.pageDeferConditions(action.page)),
            map(() => ZenitPageHitEventAction({})),
        );
    });

    /**
     * Casuística page hit al cambiar de color en ficha de producto
     */
    public setColorOnSelectedProduct$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(SetColorOnSelectedProduct),
            map(() => ZenitPageHitEventAction({})),
        );
    });

    public pageHitEventSetPickupDisplayModeAnalytics$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(SetPickupDisplayModeAnalytics),
            concatMap(() => this.actions$.pipe(ofType(SetPickupPointsViewMode)).pipe(first())),
            map(() => ZenitPageHitEventAction({})),
        );
    });

    public pageHitEventOpenModalFromComponent$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(OpenModalFromComponent),
            filter((modalData) => modalsIncluded.includes(modalData.componentName)),
            map((modalData) => ZenitPageHitEventAction({ eventFrom: EventFrom[modalData.componentName] })),
        );
    });

    /**
    * En la página del buscador, cuando se pasa de la página de búsqueda a la página de inicio del
    * buscador, se debe enviar un evento de page hit debido a que el pageType es diferente.
    */
    public pageHitSearchQueryToStart$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(ExecuteSearchAction),
            concatMap(() => this.actions$.pipe(
                ofType(ChangeTextSearchAction),
                first((updatedAction) => updatedAction.text.length === 0),
            )),
            map(() => ZenitPageHitEventAction({
                eventFrom: EventFrom.search,
            }))
        );
    });

    /**
    * En la página del buscador, cuando se pasa de la página de inicio del buscador a la página de
    * búsqueda, se debe enviar un evento de page hit debido a que el pageType es diferente.
    */
    public pageHitSearchStartToQuery$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(ChangeTextSearchAction),
            // eslint-disable-next-line rxjs/no-unsafe-first
            first(({ text }) => text.length === 0),
            concatMap(() => this.actions$.pipe(
                ofType(ExecuteSearchAction),
                filter(({ text }) => text.length > 0),
            )),
            map(() => ZenitPageHitEventAction({
                eventFrom: EventFrom.searchResults,
            }))
        );
    });

    private readonly shopcartPageUpdate$ = this.actions$.pipe(
        ofType(SetShopCartSuccess),
        filter(() => this.store.selectSignal(getPage)() === 'ItxAngularShopcartPage')
    );

    /** En la página de cart, cuando se pasa de tener items en la cesta a cesta vacía
     * o de cesta vacía a tener items en la cesta, se debe enviar un evento de page hit
     * debido a que el pageType es diferente.
     */
    public pageHitEventSetShopCartSuccess$ = createEffect(() => {
        return this.shopcartPageUpdate$.pipe(
            concatMap((action) => this.shopcartPageUpdate$.pipe(
                filter((updatedAction) =>
                    action.itemsInCart === 0 && updatedAction.itemsInCart > 0
                    || action.itemsInCart > 0 && updatedAction.itemsInCart === 0),
                first(),
            )),
            map(() => ZenitPageHitEventAction({}))
        );
    });

    public markReferrerSent$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(ZenitEventSuccess<PageHitEvent>()),
            filter(({ eventData }) => !!eventData?.referrer?.url),
            map(() => MarkReferrerSent()),
        );
    });

    public pageHitEvent$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(ZenitPageHitEventAction),
            concatLatestFrom(() => this.store.select(getCmsConfigValue('enabled_zenit'))),
            filter(([_, enabledZenit]) => enabledZenit),
            map(([action]) => this.zenitEventParserService.getEventObject<PageHitEvent>(
                ZenitEventName.PageHit,
                action.eventFrom,
                action.eventData,
            )),
            concatMap((pageHit) => this.zenitEventApiService.postEvent$(pageHit, 'hit.input.v1')
                .pipe(
                    map(() => ZenitEventSuccess<PageHitEvent>()({ eventData: pageHit })),
                    catchError((error) => of(ZenitEventError({ error })))
                )),
        );
    });
}
